import React from 'react';
import ReactDOMServer from 'react-dom/server';
import { BLOCKS } from '@contentful/rich-text-types';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import parse from 'html-react-parser';
import { getCdrEntryBindingRichTextOptions } from '@/utils/contentfulRichTextBindings';

const toArray = value => {
  return Array.isArray(value) ? value : [];
};

const DESCRIPTION_RICH_TEXT_OPTIONS = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (node, children) => <>{children}</>
  }
};

const RICH_TEXT_FRAGMENT_OPTIONS = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (node, children) => <>{children}</>
  }
};

const getAssetUrl = asset => {
  const file = asset?.file || asset?.fields?.file;
  const localizedFile = file?.['en-AU'] || file?.['en-US'];
  const url = file?.url || localizedFile?.url;

  if (!url) return undefined;

  return url.startsWith('//') ? `https:${url}` : url;
};

const getAssetDescription = asset => {
  return (
    asset?.description ||
    asset?.title ||
    asset?.fields?.description?.['en-AU'] ||
    asset?.fields?.description?.['en-US'] ||
    asset?.fields?.title?.['en-AU'] ||
    asset?.fields?.title?.['en-US']
  );
};

const toImageModule = image => {
  return {
    default: getAssetUrl(image) || image?.default || image
  };
};

const mapCardImage = asset => {
  return {
    url: getAssetUrl(asset),
    name: getAssetDescription(asset)
  };
};

const renderHtmlLabel = value => {
  return typeof value === 'string' && /<\/?[a-z][\s\S]*>/i.test(value)
    ? parse(value)
    : value;
};

const renderRichTextNodesToString = nodes => {
  const renderedNodes = documentToReactComponents(
    {
      nodeType: BLOCKS.DOCUMENT,
      data: {},
      content: toArray(nodes)
    },
    getCdrEntryBindingRichTextOptions(DESCRIPTION_RICH_TEXT_OPTIONS)
  );

  return ReactDOMServer.renderToStaticMarkup(<>{renderedNodes}</>).trim();
};

const getDescriptionNodes = node => {
  return node?.nodeType === BLOCKS.LIST_ITEM ? node.content : [node];
};

const mapRichTextDescription = richTextDoc => {
  if (!richTextDoc?.content) return [];

  const listItems = richTextDoc.content
    .filter(node => {
      return (
        node.nodeType === BLOCKS.UL_LIST || node.nodeType === BLOCKS.OL_LIST
      );
    })
    .flatMap(node => {
      return toArray(node.content);
    });

  return (listItems.length ? listItems : richTextDoc.content)
    .map(node => {
      return renderRichTextNodesToString(getDescriptionNodes(node));
    })
    .filter(Boolean);
};

const mapFilterOption = option => {
  return {
    label: option?.label,
    value: option?.value
  };
};

const mapCategoryItems = categoryItems => {
  return toArray(categoryItems)
    .map(category => {
      return category?.value;
    })
    .filter(Boolean);
};

const mapDisplayOrderItems = displayOrderItems => {
  return toArray(displayOrderItems).reduce((acc, item) => {
    const filterValue = item?.filterOption?.value;

    if (!filterValue) return acc;

    acc[filterValue] = item?.order;
    return acc;
  }, {});
};

const mapCardMetric = (value, label) => {
  return value || label
    ? {
        value,
        label: renderHtmlLabel(label)
      }
    : undefined;
};

const mapCtaItem = cta => {
  return {
    text: cta?.text,
    url: cta?.url,
    variant: cta?.variant,
    size: cta?.size,
    promocode: cta?.promoCode,
    btPromoCode: cta?.balanceTransferPromoCode
  };
};

const mapCardFilterListCard = card => {
  return {
    type: card?.type,
    category: mapCategoryItems(card?.categoryItems),
    title: card?.title,
    productid: card?.productId,
    order: mapDisplayOrderItems(card?.displayOrderItems),
    bgImage: mapCardImage(card?.backgroundImage),
    cardArt: mapCardImage(card?.cardArtImage),
    fee: mapCardMetric(card?.feeValue, card?.feeLabel),
    rate: mapCardMetric(card?.rateValue, card?.rateLabel),
    subheading: card?.subheading,
    description: mapRichTextDescription(card?.description),
    ctas: toArray(card?.ctaItems).map(mapCtaItem)
  };
};

export const getCardFilterListRebrandDataFromContentful = cardFilterList => {
  const filterOptions = toArray(cardFilterList?.filterOptionItems).map(
    mapFilterOption
  );

  return cardFilterList?.cards?.length
    ? {
        id: cardFilterList.id,
        contentfulID: cardFilterList.contentfulID,
        heading: cardFilterList.heading,
        trackingId: cardFilterList.trackingId,
        ...(filterOptions.length ? { filterOptions } : {}),
        cards: cardFilterList.cards.map(mapCardFilterListCard)
      }
    : null;
};

const renderRichTextDocument = richTextDoc => {
  return richTextDoc
    ? documentToReactComponents(
        richTextDoc,
        getCdrEntryBindingRichTextOptions(RICH_TEXT_FRAGMENT_OPTIONS)
      )
    : null;
};

const renderRichTextDocumentContent = richTextDoc => {
  if (!richTextDoc?.content) return null;

  return toArray(richTextDoc.content).map((node, index) => (
    <React.Fragment key={`${node.nodeType}-${index}`}>
      {index > 0 && node.nodeType === BLOCKS.PARAGRAPH ? (
        <>
          <br />
          <br />
        </>
      ) : null}
      {renderRichTextDocument({
        nodeType: BLOCKS.DOCUMENT,
        data: {},
        content: [node]
      })}
    </React.Fragment>
  ));
};

export const getHowToApplyRebrandV2DataFromContentful = howToApply => {
  return howToApply?.steps?.length
    ? {
        id: howToApply.id,
        contentfulID: howToApply.contentfulID,
        image: getAssetUrl(howToApply.image),
        background: howToApply.background,
        howToApply: {
          sectionTitle: howToApply.sectionTitle,
          items: howToApply.steps.map(step => ({
            title: renderRichTextDocument(step?.title),
            description: renderRichTextDocumentContent(step?.description)
          }))
        }
      }
    : null;
};

export const getAdditionalCardsRebrandDataFromContentful = additionalCards => {
  return additionalCards?.items?.length
    ? {
        id: additionalCards.id,
        contentfulID: additionalCards.contentfulID,
        heading: additionalCards.heading,
        trackingId: additionalCards.trackingId,
        items: additionalCards.items.map(item => {
          return {
            title: item?.title,
            href: item?.href,
            trackId: item?.trackId,
            imageSrc: toImageModule(item?.image),
            productid: toArray(item?.productIdItems)
          };
        })
      }
    : null;
};
